import React from 'react';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, LegalIdentifierNumberPilot, LegalIdentifierHintI18nKey, TaxFormMode } from '../constants';
import { registerTaxform } from '../taxform-store';
import { LegalIdentifierNumberField } from '../controls';
import {
  withViewTaxFormWithFilledEmptyTaxValue,
  getHelpPopupTopicFromTaxSchema,
  getPilotStatus,
  addLegalIdentifierNumberInTaxSchema,
  onTaxFormValidateFillingLegalIdentifier,
  getTaxTypeLabel,
} from '../utils';
import { validateField, CustomValidateItems } from '../validator';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  TaxLabelFormat, TaxType, TaxINPANNumberRegExp, TaxINGSTINRegExp, CountryCode,
} = Constants;
const { isPANNumberValid, isIndiaGSTINValid } = TaxUtils;

const getIndiaTaxSchema = ({ permissions, taxFormMode }) => {
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);

  const taxSchema = {
    name: 'India Taxation Validation Schema',
    type: 'object',
    properties: {
      [TaxType.GSTINNumber]: {
        type: 'string',
        maxLength: 50,
        required: false,
        pattern: TaxINGSTINRegExp,
        helpPopupTopic: 'pop_BA_Accounts_GSTIN',
      },
      [TaxType.PANNumber]: {
        type: 'string',
        maxLength: 50,
        required: true,
        pattern: TaxINPANNumberRegExp,
        helpPopupTopic: 'pop_BA_Accounts_IndiaPAN',
      },
    },
  };

  if (IsLegalIdentifierNumberPilotOn && !(taxFormMode === TaxFormMode.ViewMode)) {
    addLegalIdentifierNumberInTaxSchema(taxSchema);
  }

  return taxSchema;
};

const IndiaTaxForm = (props) => {
  const {
    i18n,
    data,
    onValidate,
    showLineByLineFormGroup,
    permissions,
    alwaysShowError,
    passiveErrors,
    disabled,
  } = props;
  const validationI18n = useValidationI18n(i18n);
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const IndiaTaxSchema = getIndiaTaxSchema(props);
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);

  const onTaxFormValidate = IsLegalIdentifierNumberPilotOn
    ? newTaxData => onTaxFormValidateFillingLegalIdentifier({
      newTaxData, onValidate, prevTaxData: data, listenedToField: TaxType.PANNumber, visuallyHidden: true,
    })
    : onValidate;

  return (
    <ValidationGroup
      validationProps={{
        schema: IndiaTaxSchema,
        i18n: validationI18n,
        onValidate: onTaxFormValidate,
        data,
      }}
    >
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.GSTINNumber, i18n, { labelFormat: TaxLabelFormat.Optional })}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: IndiaTaxSchema, taxType: TaxType.GSTINNumber })}
        helpPopupAriaLabel={i18n.getString(_TL_('GST IN number tooltip'))}
      >
        <Input
          id={InputTaxFieldId(TaxType.GSTINNumber)}
          validationProps={{
            field: TaxType.GSTINNumber,
            validator: value => validateField(
              value,
              {},
              {
                [CustomValidateItems.IndiaGSTINValidation]: inputValue => inputValue.length > 0 && !isIndiaGSTINValid(inputValue),
              },
              i18n
            ),
            alwaysShowError,
            ...passiveErrors[TaxType.GSTINNumber],
          }}
          aria-label={i18n.getString(_TL_('GST IN number'))}
          disabled={disabled}
        />

      </CustomizedFormGroup>
      <CustomizedFormGroup
        isStatic
        label={getTaxTypeLabel(TaxType.PANNumber, i18n)}
        helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: IndiaTaxSchema, taxType: TaxType.PANNumber })}
        helpPopupAriaLabel={i18n.getString(_TL_('PAN number tooltip'))}
      >
        <Input
          id={InputTaxFieldId(TaxType.PANNumber)}
          validationProps={{
            i18n: validationI18n,
            field: TaxType.PANNumber,
            validator: value => validateField(
              value,
              {},
              {
                [CustomValidateItems.PANRequired]: inputValue => inputValue.length === 0,
                [CustomValidateItems.PANValidation]: inputValue => inputValue.length > 0 && !isPANNumberValid(inputValue),
              },
              i18n
            ),
            alwaysShowError,
            ...passiveErrors[TaxType.PANNumber],
          }}
          aria-label={i18n.getString(_TL_('PAN number'))}
          disabled={disabled}
        />
      </CustomizedFormGroup>
      {IsLegalIdentifierNumberPilotOn &&
        <LegalIdentifierNumberField
          i18n={i18n}
          hintContent={i18n.getString(LegalIdentifierHintI18nKey[CountryCode.IN])}
          showLineByLineFormGroup
          alwaysShowError={alwaysShowError}
          disabled={disabled}
          visuallyHidden
        />
      }
    </ValidationGroup>
  );
};

IndiaTaxForm.propTypes = TaxTypeFormPropTypes;

registerTaxform('/IN/', IndiaTaxForm, withViewTaxFormWithFilledEmptyTaxValue({ getTaxSchema: getIndiaTaxSchema }));
